import React, { useEffect, useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Checkbox, Row, TextInput } from "@narmi/design_system";
import { Dialog } from "cerulean";
import cc from "classcat";
import { groupAccountsByOrganization } from "byzantine";
import type Account from "byzantine/src/Account";
import styles from "./OrganizationRoles.module.scss";

// Same threshold as the accounts list in the role form: longer lists get search and select all.
const LONG_ACCOUNT_LIST_THRESHOLD = 10;

interface CopyAccountPermissionsDialogProps {
  /** The account whose permissions are copied. `null` while the dialog is closed. */
  source: Account | null;
  /** Every account in the form; the source is left out of the choices. */
  accounts: Account[];
  institutionName?: string;
  onClose: () => void;
  onCopy: (targetAccountIds: string[]) => void;
}

/** Asks which accounts to copy one account's access and permissions to. */
export default function CopyAccountPermissionsDialog({
  source,
  accounts,
  institutionName,
  onClose,
  onCopy,
}: CopyAccountPermissionsDialogProps) {
  const { l10n } = useLocalization();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  const sourceId = source ? String(source.id) : null;
  const targets = useMemo(
    () => accounts.filter((account) => String(account.id) !== sourceId),
    [accounts, sourceId]
  );

  // Each opening starts from a clean choice.
  useEffect(() => {
    setSelectedIds(new Set());
    setQuery("");
  }, [sourceId]);

  const groupedAccounts = useMemo(
    () =>
      groupAccountsByOrganization(targets, institutionName) as unknown as Map<
        string,
        Account[]
      >,
    [targets, institutionName]
  );
  const hasMultipleOrgs = groupedAccounts.size > 1;
  const isLongList = targets.length > LONG_ACCOUNT_LIST_THRESHOLD;
  const allSelected =
    targets.length > 0 && targets.every((a) => selectedIds.has(String(a.id)));

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (account: Account) =>
    !normalizedQuery ||
    account.getShortDescription().toLowerCase().includes(normalizedQuery);

  const toggle = (accountId: string) =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(targets.map((a) => String(a.id)))
    );

  const groups: [string, Account[]][] = hasMultipleOrgs
    ? Array.from(groupedAccounts.entries())
    : [["", targets]];
  const visibleGroups = groups
    .map(([orgName, list]) => [orgName, list.filter(matchesQuery)] as const)
    .filter(([, list]) => list.length > 0);

  const renderTarget = (account: Account) => {
    const accountId = String(account.id);
    return (
      <div key={accountId} className={styles.copyTargetCard}>
        <Checkbox
          kind="condensed"
          label={
            (
              <span className={styles.accountLabel}>
                <h4 className={styles.accountName}>{account.getName()}</h4>
                <span className={styles.accountMask}>
                  {account.getMaskedNumber()}
                </span>
              </span>
            ) as unknown as string
          }
          checked={selectedIds.has(accountId)}
          onChange={() => toggle(accountId)}
        />
      </div>
    );
  };

  return (
    <Dialog
      isOpen={Boolean(source)}
      onUserDismiss={onClose}
      title={
        source
          ? l10n.getString("org-role-copy-dialog-title", {
              account: source.getShortDescription(false),
            })
          : ""
      }
      width="568px"
      footer={
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("org-role-copy-dialog-close")}
              onClick={onClose}
              testId="copy-permissions-dialog-close"
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("org-role-copy-dialog-confirm")}
              onClick={() => onCopy(Array.from(selectedIds))}
              isDisabled={selectedIds.size === 0}
              testId="copy-permissions-dialog-confirm"
            />
          </Row.Item>
        </Row>
      }
    >
      <p>{l10n.getString("org-role-copy-dialog-description")}</p>
      {isLongList && (
        <div className={styles.accountsToolbar}>
          <div className={styles.accountsSearch}>
            <TextInput
              label={l10n.getString("org-role-accounts-search-placeholder")}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              startIcon="search"
            />
          </div>
          <span className={styles.accountsCount} />
          <Checkbox
            label={l10n.getString("org-role-accounts-select-all", {
              count: targets.length,
            })}
            checked={allSelected}
            onChange={toggleAll}
          />
        </div>
      )}
      <div
        className={cc({
          [styles.copyTargetsScrollable]: isLongList,
        })}
      >
        {visibleGroups.map(([orgName, list]) => (
          <div key={orgName || "accounts"} className={styles.orgGroupContainer}>
            {orgName && <div className={styles.orgGroupName}>{orgName}</div>}
            <div className={styles.copyTargetList}>
              {list.map(renderTarget)}
            </div>
          </div>
        ))}
        {visibleGroups.length === 0 && (
          <div className={styles.noResults}>
            {l10n.getString("org-role-accounts-no-results")}
          </div>
        )}
      </div>
    </Dialog>
  );
}
